import { useState, useMemo } from 'react';
import { VisitRow } from './VisitRow';
import { Pagination } from '../common/Pagination';
import { Spinner } from '../common/Spinner';
import { filterVisits } from '../../utils/visits';
import type { Visit } from '../../types';

interface VisitListPageProps {
  visits: Visit[];
  isLoading: boolean;
  emptyMessage: string;
  searchEmptyMessage?: string;
  onVisitClick: (visit: Visit) => void;
  showStatus?: boolean;
  timeWidth?: string;
  dateFilter?: React.ReactNode;
  filterFn?: (visits: Visit[]) => Visit[];
  heading?: React.ReactNode;
}

const PAGE_SIZE = 10;

export function VisitListPage({
  visits,
  isLoading,
  emptyMessage,
  searchEmptyMessage = 'No matching visits',
  onVisitClick,
  showStatus,
  timeWidth,
  dateFilter,
  filterFn,
  heading,
}: VisitListPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const filteredVisits = useMemo(() => {
    let result = filterFn ? filterFn(visits) : visits;
    result = filterVisits(result, searchQuery);
    return result;
  }, [visits, searchQuery, filterFn]);

  const totalPages = Math.max(1, Math.ceil(filteredVisits.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageVisits = filteredVisits.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (isLoading) {
    return <div className="page-loading" style={{ padding: '2rem' }}><Spinner /></div>;
  }

  return (
    <>
      <div className="card">
        <div className="card-header">
          {heading || <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>{dateFilter}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            {heading && dateFilter}
            <input
              type="text"
              className="search-input"
              placeholder="search..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        {filteredVisits.length === 0 ? (
          <p className="text-muted text-sm">{searchQuery ? searchEmptyMessage : emptyMessage}</p>
        ) : (
          <>
            {pageVisits.map((visit, i) => (
              <VisitRow
                key={visit.roomId || visit.visitId || i}
                visit={visit}
                index={(safePage - 1) * PAGE_SIZE + i}
                onClick={() => onVisitClick(visit)}
                showStatus={showStatus}
                timeWidth={timeWidth}
              />
            ))}
            <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>
    </>
  );
}
